/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Avatar } from '@/shared/ui/Avatar'
import { Spinner } from '@/shared/ui/Spinner'
import { Modal } from '@/shared/ui/Modal'
import { Button } from '@/shared/ui/Button'
import { Pagination } from '@/shared/ui/Pagination'
import { cn } from '@/shared/utils'
import { Send, Search, X, Inbox, Plus, UserCheck, MessageSquare, Mail, User as UserIcon, Lock, RotateCcw, Clock, History } from 'lucide-react'
import {
  useConversations, useMessages, useSendMessage, useMarkRead, useChatSocket,
  useAdminStartConversation, useAssignConversation, useSetConversationStatus,
  useAgents, useUserSearch,
} from '@/api/hooks'
import { useDebounce } from '@/shared/hooks'
import { toast } from '@/store/useToastStore'
import { getApiError } from '@/api/client'
import type { ChatConversation, ChatMessage, ChatUser } from '@/api/services/chatService'

const STATUSES = ['open', 'pending', 'resolved', 'closed']

const dayTime = (s?: string | null) => {
  if (!s) return ''
  const d = new Date(s.replace(' ', 'T'))
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// Relative "5m ago" only within the last hour, for scannability — anything older
// switches to the full day/date/time so it doesn't read as vaguely "a while ago".
function timeAgo(t: (key: string, opts?: any) => string, s?: string | null): string | null {
  if (!s) return null
  const d = new Date(s.replace(' ', 'T'))
  if (isNaN(d.getTime())) return null
  const sec = Math.max(0, (Date.now() - d.getTime()) / 1000)
  if (sec < 60) return t('supportTickets.justNow')
  const min = Math.floor(sec / 60)
  if (min < 60) return t('supportTickets.minutesAgo', { n: min })
  return dayTime(s)
}

function statusPill(status?: string) {
  const u = (status ?? '').toLowerCase()
  if (u === 'open')     return 'bg-emerald-50 text-emerald-600'
  if (u === 'pending')  return 'bg-amber-50 text-amber-600'
  if (u === 'resolved') return 'bg-sky-50 text-sky-600'
  if (u === 'closed')   return 'bg-gray-100 text-gray-500'
  return 'bg-gray-100 text-gray-500'
}
function statusBar(status?: string) {
  const u = (status ?? '').toLowerCase()
  if (u === 'open')     return 'bg-emerald-400'
  if (u === 'pending')  return 'bg-amber-400'
  if (u === 'resolved') return 'bg-sky-400'
  return 'bg-gray-300'
}
// ── Ticket card (grid) ────────────────────────────────────────────────────────
function TicketCard({ ticket, onOpen }: { ticket: ChatConversation; onOpen: () => void }) {
  const { t } = useTranslation()
  const user = ticket.owner
  return (
    <div className="text-start bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
      <div className={cn('h-1 w-full', statusBar(ticket.status))} aria-hidden="true" />
      <div className="p-4 flex items-start justify-between gap-3 border-b border-gray-50">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative">
            <Avatar name={user?.name ?? '—'} src={user?.photo_url} size="md" />
            {ticket.unread > 0 && <span className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center text-[10px] font-semibold text-white bg-brand-500 rounded-full ring-2 ring-white">{ticket.unread}</span>}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">{user?.name ?? t('chat.unknownUser')}</div>
            <div className="text-xs text-gray-400 truncate flex items-center gap-1"><Mail size={10} />{(user as any)?.email ?? user?.phone ?? '—'}</div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 flex-1 space-y-2">
        {ticket.subject && <div className="text-sm font-bold text-gray-800 truncate">{ticket.subject}</div>}
        <p className="text-xs text-gray-600 leading-relaxed line-clamp-2 flex items-start gap-1.5">
          <MessageSquare size={12} className="text-gray-300 flex-shrink-0 mt-0.5" />
          {/* Always the ticket's original opening message, not whatever was said most recently. */}
          <span>{ticket.message || ticket.last_message?.message || t('supportTickets.noMessage')}</span>
        </p>
        {ticket.assignee?.name && (
          <div className="text-[11px] text-gray-400 flex items-center gap-1">
            <UserIcon size={11} /> {t('supportTickets.assignedTo', { name: ticket.assignee.name })}
          </div>
        )}
      </div>

      {/* Created / updated */}
      <div className="px-4 pb-2 flex items-center gap-3 text-[11px] text-gray-400 border-t border-gray-50 pt-2">
        <span className="inline-flex items-center gap-1" title={dayTime(ticket.created_at)}>
          <Clock size={10} className="flex-shrink-0" /> {t('supportTickets.created')} {timeAgo(t, ticket.created_at) ?? '—'}
        </span>
        {ticket.updated_at !== ticket.created_at && (
          <span className="inline-flex items-center gap-1" title={dayTime(ticket.updated_at)}>
            <History size={10} className="flex-shrink-0" /> {t('supportTickets.updated')} {timeAgo(t, ticket.updated_at) ?? '—'}
          </span>
        )}
      </div>

      <div className="px-4 pb-4 pt-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={cn('px-2.5 py-1 rounded-full text-[11px] font-bold', statusPill(ticket.status))}>
            {t(`chat.status.${ticket.status}`, ticket.status)}
          </span>
          {ticket.unread > 0 ? (
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-red-50 text-red-600 border border-red-200">
              {t('supportTickets.notRead')}
            </span>
          ) : ticket.last_message?.sender_type === 'user' && (
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-600 border border-amber-200">
              {t('supportTickets.notReplied')}
            </span>
          )}
        </div>
        <button onClick={onOpen} className="text-[11px] text-brand-600 font-semibold inline-flex items-center gap-1 hover:text-brand-700">
          <MessageSquare size={12} /> {t('support.openChat')}
        </button>
      </div>
    </div>
  )
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 animate-pulse overflow-hidden">
      <div className="h-1 w-full bg-gray-100" />
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-1.5"><div className="h-3 bg-gray-200 rounded w-2/3" /><div className="h-2 bg-gray-100 rounded w-1/2" /></div>
      </div>
      <div className="px-4 pb-2 space-y-1.5"><div className="h-2 bg-gray-100 rounded w-full" /><div className="h-2 bg-gray-100 rounded w-3/4" /></div>
      <div className="px-4 pb-2 pt-2 border-t border-gray-50"><div className="h-2 bg-gray-100 rounded w-2/3" /></div>
      <div className="px-4 pb-4 pt-1"><div className="h-6 bg-gray-100 rounded-full w-20" /></div>
    </div>
  )
}

export function SupportInboxPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const debounced = useDebounce(search, 350)
  const [activeId, setActiveId] = useState<number | null>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [page, setPage] = useState(1)
  const PER_PAGE = 12

  // Filters change the result set → jump back to the first page.
  useEffect(() => { setPage(1) }, [debounced, statusFilter])

  const convQ = useConversations({ search: debounced || undefined, status: statusFilter || undefined, page, limit: PER_PAGE }, 8_000)
  // Most recently active conversation (new message, status change, ...) first.
  const conversations: ChatConversation[] = useMemo(() => {
    const list = convQ.data?.conversations ?? []
    return [...list].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
  }, [convQ.data])
  const total = convQ.data?.total ?? 0
  const perPage = convQ.data?.count_per_page ?? PER_PAGE
  const lastPage = Math.max(1, Math.ceil(total / perPage))

  // Live updates over WebSocket: refresh list (+ the open ticket) on any event.
  const onEvent = useCallback((e: any) => {
    if (e?.conversation_id) qc.invalidateQueries({ queryKey: ['chat', 'messages', e.conversation_id] })
    qc.invalidateQueries({ queryKey: ['chat', 'conversations'] })
    qc.invalidateQueries({ queryKey: ['chat', 'unread'] })
  }, [qc])
  useChatSocket(onEvent, true)

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('supportTickets.title')}
        breadcrumbs={[{ label: t('supportTickets.breadcrumb.dashboard'), to: '/dashboard' }, { label: t('supportTickets.breadcrumb.support') }]}
      />

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-56 flex-1 max-w-md">
            <Search size={13} className="text-gray-400 flex-shrink-0" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('supportTickets.searchPlaceholder')}
              className="text-sm text-gray-700 placeholder-gray-400 focus:outline-none flex-1 bg-transparent min-w-0" />
            {search && <button onClick={() => setSearch('')}><X size={12} className="text-gray-300 hover:text-gray-500" /></button>}
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white min-w-36 focus:outline-none focus:ring-2 focus:ring-brand-400">
            <option value="">{t('supportTickets.allStatuses')}</option>
            {STATUSES.map(s => <option key={s} value={s}>{t(`chat.status.${s}`, s)}</option>)}
          </select>
          <div className="ms-auto">
            <Button icon={<Plus size={15} />} onClick={() => setNewOpen(true)}>{t('support.newTicket')}</Button>
          </div>
        </div>
      </Card>

      {convQ.isError && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          <X size={16} className="flex-shrink-0" /> {t('supportTickets.loadFailed')}
        </div>
      )}

      {convQ.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : conversations.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-full bg-brand-50 flex items-center justify-center mb-3">
            <Inbox size={24} className="text-brand-400" />
          </div>
          <p className="text-sm font-semibold text-gray-600">{t('support.empty')}</p>
          {search || statusFilter ? (
            <button onClick={() => { setSearch(''); setStatusFilter('') }} className="mt-3 text-xs font-semibold text-brand-600 hover:text-brand-700 underline">
              {t('supportTickets.clearFilters')}
            </button>
          ) : (
            <>
              <p className="text-xs text-gray-400 mt-1 max-w-xs">{t('supportTickets.newTicketCta')}</p>
              <Button className="mt-4" icon={<Plus size={15} />} onClick={() => setNewOpen(true)}>{t('support.newTicket')}</Button>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {conversations.map(c => <TicketCard key={c.id} ticket={c} onOpen={() => setActiveId(c.id)} />)}
        </div>
      )}

      {!convQ.isLoading && total > 0 && (
        <Card padding={false}>
          <Pagination page={page} lastPage={lastPage} total={total} perPage={perPage} onPageChange={p => setPage(Math.min(Math.max(1, p), lastPage))} />
        </Card>
      )}

      <ChatModal conversation={conversations.find(c => c.id === activeId) ?? null} open={activeId != null} onClose={() => setActiveId(null)} />
      <NewTicketModal open={newOpen} onClose={() => setNewOpen(false)} onStarted={id => { setNewOpen(false); setActiveId(id) }} />
    </div>
  )
}

// ── Chat modal (animated, centered — opens on card click) ─────────────────────
function ChatModal({ conversation, open, onClose }: { conversation: ChatConversation | null; open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const activeId = conversation?.id ?? null

  // Two-phase mount so the entrance transition actually plays: keep the node in
  // the tree briefly after close for the exit animation, and flip `show` on the
  // next frame after open for the enter animation.
  const [mounted, setMounted] = useState(false)
  const [show, setShow] = useState(false)
  useEffect(() => {
    if (open) {
      setMounted(true)
      const id = requestAnimationFrame(() => setShow(true))
      return () => cancelAnimationFrame(id)
    }
    setShow(false)
    const id = setTimeout(() => setMounted(false), 250)
    return () => clearTimeout(id)
  }, [open])

  // Lock background scroll while open.
  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden' } else { document.body.style.overflow = '' }
    return () => { document.body.style.overflow = '' }
  }, [open])
  // Poll the open thread every 2.5s as a fallback to the live WebSocket.
  const msgQ = useMessages(open ? (activeId ?? undefined) : undefined, open ? 2_500 : undefined)
  const messages: ChatMessage[] = msgQ.data?.messages ?? []
  const header = msgQ.data?.conversation
  const owner = header?.owner ?? conversation?.owner ?? null
  const assignedTo = header?.assigned_to ?? conversation?.assigned_to ?? null
  const status = header?.status ?? conversation?.status ?? 'open'
  const subject = header?.subject ?? conversation?.subject ?? ''
  const locked = status === 'resolved' || status === 'closed'

  const sendMessage = useSendMessage()
  const markRead = useMarkRead()
  const assign = useAssignConversation()
  const setStatus = useSetConversationStatus()
  const agents: ChatUser[] = useAgents(open).data?.agents ?? []
  const [draft, setDraft] = useState('')

  // Mark read on open when there are unread messages.
  useEffect(() => {
    if (open && activeId && conversation && conversation.unread > 0) markRead.mutate(activeId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeId, conversation?.unread])

  useEffect(() => { if (!open) setDraft('') }, [open])

  const bottomRef = useRef<HTMLDivElement>(null)
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages.length, open])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const text = draft.trim()
    if (!text || !activeId) return
    setDraft('')
    sendMessage.mutate({ id: activeId, message: text })
  }
  const onAssign = (v: string) => activeId && assign.mutate({ id: activeId, assignedTo: v ? Number(v) : null }, { onError: err => toast.error(getApiError(err)) })
  const onStatus = (v: string) => activeId && setStatus.mutate({ id: activeId, status: v }, { onError: err => toast.error(getApiError(err)) })

  if (!mounted) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className={cn('absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300', show ? 'opacity-100' : 'opacity-0')} onClick={onClose} />
      {/* Centered panel */}
      <div className={cn(
        'relative w-full max-w-2xl h-[82vh] max-h-[720px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden',
        'transition-all duration-300 ease-out will-change-transform',
        show ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
      )}>
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar name={owner?.name || '—'} src={owner?.photo_url} size="md" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{owner?.name || t('chat.unknownUser')}</p>
              <p className="text-xs text-gray-400 truncate">{owner?.phone || ''}{subject ? `${owner?.phone ? ' · ' : ''}${subject}` : ''}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 text-gray-400 shrink-0"><X size={16} /></button>
        </div>

        {/* Controls */}
        <div className="px-5 py-2.5 border-b border-gray-100 flex items-center gap-2 flex-wrap bg-gray-50/60">
          <label className="flex items-center gap-1 text-gray-400 text-xs">
            <UserCheck size={13} />
            <select value={assignedTo ?? ''} onChange={e => onAssign(e.target.value)} disabled={assign.isPending}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white">
              <option value="">{t('chat.unassigned')}</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.name || `#${a.id}`}</option>)}
            </select>
          </label>
          <select value={status} onChange={e => onStatus(e.target.value)} disabled={setStatus.isPending}
            className={cn('text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-400 border-0 font-medium', statusPill(status))}>
            {STATUSES.map(s => <option key={s} value={s}>{t(`chat.status.${s}`, s)}</option>)}
          </select>
        </div>

        {/* Original request — pinned so it stays visible even once the thread scrolls. */}
        {conversation?.message && (
          <div className="px-5 py-2.5 border-b border-gray-100 bg-amber-50/60 text-xs text-gray-600">
            <span className="font-semibold text-gray-700">{t('support.originalRequest')}: </span>
            <span className="whitespace-pre-wrap">{conversation.message}</span>
          </div>
        )}

        {/* Thread */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-gray-50/50">
          {msgQ.isLoading && <div className="flex justify-center py-8"><Spinner /></div>}
          {messages.map(m => {
            const mine = m.sender_type === 'admin' || m.sender_type === 'agent'
            return (
              <div key={m.id} className={cn('flex', mine ? 'justify-end' : 'justify-start')}>
                <div className={cn('max-w-[78%] rounded-2xl px-4 py-2 text-sm',
                  mine ? 'bg-brand-500 text-white rounded-br-sm' : 'bg-white border border-gray-100 text-gray-800 rounded-bl-sm')}>
                  <p className="whitespace-pre-wrap break-words">{m.message}</p>
                  <span className={cn('block text-[10px] mt-1', mine ? 'text-white/70' : 'text-gray-400')}>
                    {dayTime(m.created_at)}{mine && m.read_at ? ` · ${t('chat.seen')}` : ''}
                  </span>
                </div>
              </div>
            )
          })}
          <div ref={bottomRef} />
        </div>

        {/* Composer — locked when the ticket is resolved/closed */}
        {locked ? (
          <div className="p-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs text-gray-500 inline-flex items-center gap-1.5">
              <Lock size={13} /> {t('support.closedNotice')}
            </span>
            <Button size="sm" variant="outline" icon={<RotateCcw size={13} />} loading={setStatus.isPending} onClick={() => onStatus('open')}>
              {t('support.reopen')}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-3 border-t border-gray-100 flex items-center gap-2">
            <input value={draft} onChange={e => setDraft(e.target.value)} placeholder={t('chat.messagePlaceholder')}
              className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-400" />
            <button type="submit" disabled={!draft.trim() || sendMessage.isPending}
              className="w-10 h-10 shrink-0 inline-flex items-center justify-center rounded-full bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-40">
              <Send size={16} />
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

// ── New ticket: search a user, then send the first message ────────────────────
function NewTicketModal({ open, onClose, onStarted }: { open: boolean; onClose: () => void; onStarted: (id: number) => void }) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const debounced = useDebounce(q, 350)
  const [selected, setSelected] = useState<ChatUser | null>(null)
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const users: ChatUser[] = useUserSearch(debounced, open).data?.users ?? []
  const start = useAdminStartConversation()

  useEffect(() => { if (open) { setQ(''); setSelected(null); setSubject(''); setMessage('') } }, [open])

  const submit = () => {
    if (!selected || !message.trim()) return
    start.mutate({ user_id: selected.id, subject: subject.trim() || undefined, message: message.trim() }, {
      onSuccess: (d: any) => onStarted(Number(d?.id)),
      onError: err => toast.error(getApiError(err)),
    })
  }

  return (
    <Modal open={open} onClose={onClose} title={t('support.newTicket')} size="md">
      <div className="space-y-4">
        {!selected ? (
          <>
            <div className="relative">
              <Search size={15} className="absolute start-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={t('chat.searchUserPlaceholder')}
                className="w-full ps-9 pe-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
            </div>
            <div className="max-h-64 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
              {users.length === 0 && <p className="text-sm text-gray-400 text-center py-6">{t('chat.noUsers')}</p>}
              {users.map(u => (
                <button key={u.id} onClick={() => setSelected(u)} className="w-full text-start px-4 py-2.5 hover:bg-gray-50 transition-colors flex items-center gap-3">
                  <Avatar name={u.name || '—'} src={u.photo_url} size="sm" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900 block truncate">{u.name || `#${u.id}`}</span>
                    <span className="text-xs text-gray-400">{u.phone || ''}{u.user_type ? ` · ${u.user_type}` : ''}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-lg bg-brand-50 px-4 py-2.5">
              <div className="flex items-center gap-3">
                <Avatar name={selected.name || '—'} src={selected.photo_url} size="sm" />
                <div>
                  <span className="text-sm font-medium text-gray-900 block">{selected.name || `#${selected.id}`}</span>
                  <span className="text-xs text-gray-500">{selected.phone || ''}</span>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-xs text-brand-600 font-medium">{t('chat.change')}</button>
            </div>
            <input value={subject} onChange={e => setSubject(e.target.value)} placeholder={t('chat.subjectPlaceholder')}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} placeholder={t('chat.messagePlaceholder')}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none" />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
              <Button icon={<Send size={14} />} loading={start.isPending} disabled={!message.trim()} onClick={submit}>{t('chat.send')}</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
